import React, { useState, useEffect, useRef } from "react";
import {
  Users,
  DollarSign,
  BarChart3,
  FileBarChart,
  PhoneCall,
  AlertTriangle,
} from "lucide-react";
import { FaUser, FaBars, FaTimes } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { BASE_URL } from "../config";
import { BsSpeedometer } from "react-icons/bs";

const DashboardHeader = () => {
  const [user, setUser] = useState(null);
  const [open, setOpen] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);

  const dropdownRef = useRef();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchUser = async () => {
      const res = await fetch(`${BASE_URL}session.php`, {
        credentials: "include",
      });
      const data = await res.json();
      if (data.status === "success") setUser(data.user);
    };
    fetchUser();
  }, []);

  const handleLogout = async () => {
    await fetch(`${BASE_URL}logout.php`, {
      method: "POST",
      credentials: "include",
    });
    navigate("/login");
  };

  const cards = [
    { title: "Home", icon: <BsSpeedometer size={16} />, to: "/analytics-dashboard" },
    { title: "Dashboard", icon: <BsSpeedometer size={16} />, to: "/admin" },
    { title: "Users", icon: <Users size={16} />, to: "/users" },
    { title: "Op Cost", icon: <DollarSign size={16} />, to: "/operationscost" },
    { title: "Mkt Cost", icon: <BarChart3 size={16} />, to: "/marketingcost" },
    { title: "Productivity", icon: <FileBarChart size={16} />, to: "/marketing-expense-table" },
    { title: "Calls", icon: <PhoneCall size={16} />, to: "/call-logs" },
    { title: "Loss", icon: <AlertTriangle size={16} />, to: "/chargeback" },
  ];

  const isAdmin = user?.role?.toLowerCase() === "admin";

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <header className="w-full bg-[#11182b] border-b border-gray-800 shadow-lg">

      {/* ================= DESKTOP / TABLET ================= */}
      <div className="hidden md:flex items-center w-full px-4 py-3 gap-4">

        {/* LOGO */}
        <img src="/assets/convex-logo.png" className="h-8 flex-shrink-0" />

        {/* NAV (SCROLLABLE FIX) */}
        {isAdmin && (
          <div className="flex-1 overflow-x-auto no-scrollbar">
            <div className="flex gap-2 min-w-max">

              {cards.map((card, i) => {
                const active = location.pathname === card.to;

                return (
                  <button
                    key={i}
                    onClick={() => navigate(card.to)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs whitespace-nowrap transition
                      ${
                        active
                          ? "text-cyan-400 bg-[#1a2337]"
                          : "text-gray-300 hover:bg-[#1a2337] hover:text-white"
                      }`}
                  >
                    {card.icon}
                    {card.title}
                  </button>
                );
              })}

            </div>
          </div>
        )}

        {/* USER */}
        <div className="relative flex-shrink-0" ref={dropdownRef}>
          <div
            onClick={() => setOpen(!open)}
            className="flex items-center gap-2 px-3 py-2 bg-[#1f2a48] rounded-full cursor-pointer"
          >
            <FaUser className="text-blue-400" />
            <span className="text-sm text-gray-200">{user?.name}</span>
          </div>

          {open && (
            <div className="absolute right-0 mt-2 w-36 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-50">
              <button
                onClick={handleLogout}
                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-700"
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ================= MOBILE ================= */}
      <div className="md:hidden flex items-center justify-between px-4 py-3">
        <button onClick={() => setMobileMenu(!mobileMenu)}>
          {mobileMenu ? <FaTimes /> : <FaBars />}
        </button>

        <img src="/assets/convex-logo.png" className="h-8" />
      </div>

      {/* MOBILE MENU */}
      {mobileMenu && isAdmin && (
        <div className="bg-[#0f1629] border-t border-gray-700 px-3 py-3 md:hidden">
          {cards.map((item, i) => (
            <button
              key={i}
              onClick={() => {
                navigate(item.to);
                setMobileMenu(false);
              }}
              className="w-full text-left px-3 py-3 text-gray-300 hover:bg-[#1a2337] rounded-md mb-1"
            >
              {item.title}
            </button>
          ))}
        </div>
      )}
    </header>
  );
};

export default DashboardHeader;